/**
 * section-distributor-locator.js - OpenStreetMap (Leaflet) & Pagination Edition
 * Wokiee Theme - Distributor Locator
 *
 * - Free, no API Key needed (OpenStreetMap tiles + Leaflet.js)
 * - Split Desktop Layout: 2 Columns of Cards + Right Sticky Interactive Map
 * - Automatic 100 km radius filter with Geolocation
 * - Maximum 18 cards per page with smart pagination
 * - Interactive synchronization between cards, pagination, and map markers
 */

(function () {
  'use strict';

  const DEFAULT_RADIUS_KM = 100;
  const DEFAULT_PER_PAGE = 18;

  // -- Icon helpers ---------------------------------------------
  const ICON = {
    pin:          `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
    clock:        `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
    phone:        `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
    globe:        `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
    instagram:    `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>`,
    maplink:      `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>`,
    locme:        `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.94 11a8 8 0 1 0-7.94 8.94"/><path d="M22 12h-4"/><path d="M12 22v-4"/><path d="M12 2v4"/><path d="M2 12h4"/></svg>`,
    warning:      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
    close:        `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
    chevronLeft:  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>`,
    chevronRight: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>`,
    target:       `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2"/><line x1="12" y1="1" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="23"/><line x1="1" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="23" y2="12"/></svg>`,
  };

  // -- Region colour palette -------------------------------------
  const REGION_COLORS = [
    '#033097','#1d3557','#9b2226','#6d3b47',
    '#583101','#3d405b','#4a4e69','#22577a',
    '#344e41','#7b2d8b','#c05c2e','#2b6cb0',
  ];

  function getRegionColor(region, regionList) {
    const idx = regionList.indexOf(region);
    return REGION_COLORS[idx % REGION_COLORS.length] || '#033097';
  }

  // -- Haversine distance (km) -----------------------------------
  function haversine(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) *
              Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.asin(Math.sqrt(a));
  }

  // -- Road Distance Estimation (Circuity Factor for Chilean Street Networks) --
  function estimateRoadDistance(straightKm) {
    if (straightKm <= 0) return 0;
    // Urban network circuity factor: ~1.28x for city streets (<5km), smoothly transitioning to 1.15x for highways (>30km)
    let factor = 1.28;
    if (straightKm > 30) {
      factor = 1.15;
    } else if (straightKm > 5) {
      factor = 1.28 - ((straightKm - 5) / 25) * (1.28 - 1.15);
    }
    return straightKm * factor;
  }

  function formatRoadDistance(roadKm) {
    if (roadKm == null || isNaN(roadKm) || !isFinite(roadKm)) return '';
    if (roadKm < 1) {
      const meters = Math.round(roadKm * 1000);
      return `a ${meters} m`;
    }
    if (roadKm < 10) {
      return `a ${roadKm.toFixed(1)} km`;
    }
    return `a ${Math.round(roadKm)} km`;
  }

  function formatDuration(seconds) {
    if (!seconds || isNaN(seconds) || seconds <= 0) return '';
    const mins = Math.round(seconds / 60);
    if (mins < 60) {
      return `${mins} min`;
    }
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    if (remMins === 0) return `${hours} h`;
    return `${hours} h ${remMins} min`;
  }

  function formatExactRouteBadge(distMeters, durSec) {
    const distKm = distMeters / 1000;
    let distText = '';
    if (distKm < 1) {
      distText = `a ${Math.round(distMeters)} m`;
    } else if (distKm < 10) {
      distText = `a ${distKm.toFixed(1)} km`;
    } else {
      distText = `a ${Math.round(distKm)} km`;
    }

    const durText = formatDuration(durSec);
    if (durText) {
      return `${ICON.target} <span>${distText}</span> <span class="dloc-dist-time">(${durText})</span>`;
    }
    return `${ICON.target} <span>${distText}</span>`;
  }

  // -- Geolocation helpers ---------------------------------------
  function getBrowserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error('no-geolocation'));
      navigator.geolocation.getCurrentPosition(
        pos => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          source: 'gps'
        }),
        err => {
          // If high accuracy times out, try once with relaxed settings before giving up to IP
          navigator.geolocation.getCurrentPosition(
            pos => resolve({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              source: 'browser'
            }),
            err2 => reject(err2),
            { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 }
          );
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
      );
    });
  }

  function getIPLocation() {
    return fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) })
      .then(r => r.json())
      .then(d => {
        if (d.latitude && d.longitude) {
          return { lat: d.latitude, lng: d.longitude, city: d.city, source: 'ip' };
        }
        throw new Error('ip-no-coords');
      });
  }

  async function detectLocation() {
    try {
      return await getBrowserLocation();
    } catch (browserErr) {
      try {
        return await getIPLocation();
      } catch {
        return null;
      }
    }
  }

  // -- URL & Social helpers ---------------------------------------
  function ensureUrl(url) {
    if (!url) return null;
    let s = String(url).trim();
    if (!s || s === '*' || s === '-' || s === 'n/a' || s === 'na' || s === 'null' || s === 'undefined' || s === '0' || s === 'no' || s === 'none' || s === 'no tiene' || /^\d+$/.test(s)) return null;
    s = s.replace(/,+/g, '.').replace(/\s+/g, '');
    if (!s.includes('.') && !s.startsWith('http')) return null;
    return s.startsWith('http') ? s : 'https://' + s;
  }

  function detectSocialOrWeb(value) {
    if (!value) return null;
    let s = String(value).trim();
    if (!s) return null;

    const lower = s.toLowerCase();
    if (['*', '-', 'n/a', 'na', 'null', 'undefined', '0', 'no', 'none', 'no tiene', 'sin rrss', 'sin instagram', 'no aplica', 'no cuenta'].includes(lower)) {
      return null;
    }

    // Ignore pure numbers (such as row IDs, internal store codes, or postal numbers)
    if (/^\d+$/.test(s)) return null;

    // 1. Explicit Instagram URL
    const igUrlMatch = s.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9._-]+)\/?/i);
    if (igUrlMatch && igUrlMatch[1]) {
      const user = igUrlMatch[1].replace(/\/$/, '');
      if (user && !['p', 'reel', 'stories', 'explore', 'direct'].includes(user.toLowerCase()) && !/^\d+$/.test(user)) {
        return { type: 'instagram', url: `https://www.instagram.com/${user}/`, label: 'Instagram' };
      } else if (user) {
        const full = igUrlMatch[0].startsWith('http') ? igUrlMatch[0] : 'https://' + igUrlMatch[0];
        return { type: 'instagram', url: full, label: 'Instagram' };
      }
    }

    // 2. Explicit Facebook URL
    const fbUrlMatch = s.match(/(?:https?:\/\/)?(?:www\.)?(?:facebook\.com|fb\.me|fb\.com)\/[a-zA-Z0-9._-]+/i);
    if (fbUrlMatch) {
      const full = fbUrlMatch[0].startsWith('http') ? fbUrlMatch[0] : 'https://' + fbUrlMatch[0];
      return { type: 'web', url: full, label: 'Facebook' };
    }

    // 3. Explicit prefix like "IG:", "Instagram:", "Insta:"
    const prefixMatch = s.match(/(?:ig|insta|instagram)\s*[:\-]?\s*@?([a-zA-Z0-9._-]+)/i);
    if (prefixMatch && prefixMatch[1]) {
      const user = prefixMatch[1];
      if (user.length >= 2 && !/^\d+$/.test(user) && !['null', 'undefined', 'na', 'no'].includes(user.toLowerCase())) {
        return { type: 'instagram', url: `https://www.instagram.com/${user}/`, label: 'Instagram' };
      }
    }

    // 4. Handle starting with @ (e.g. @superfelines) -> Instagram
    const atMatch = s.match(/^@([a-zA-Z0-9._-]+)/);
    if (atMatch && atMatch[1]) {
      const user = atMatch[1];
      if (user.length >= 2 && !/^\d+$/.test(user)) {
        return { type: 'instagram', url: `https://www.instagram.com/${user}/`, label: 'Instagram' };
      }
    }

    // 5. Website URL / Domain (e.g. WWW.SUPERFELINES.CL, https://superfelines.cl, superfelines.cl, www.petshop.com)
    const cleanNoHttp = s.replace(/^https?:\/\//i, '').replace(/[\s/?#].*$/, '').toLowerCase();
    const isWebDomain = cleanNoHttp.startsWith('www.') || /\.(?:cl|com|org|net|io|store|shop|site|vet|lat|online|co|biz|info|edu|gob)(?:\/|$)/i.test(s.toLowerCase());
    
    if (isWebDomain || s.startsWith('http://') || s.startsWith('https://')) {
      const webUrl = s.startsWith('http') ? s : 'https://' + s;
      return { type: 'web', url: webUrl, label: 'Web' };
    }

    // 6. Plain alphanumeric username with no domain extension (e.g. "superfelines", "vet_amigos")
    const cleanUser = s
      .replace(/^@/, '')
      .split(/[\s/?#]/)[0]
      .trim();

    if (!cleanUser || cleanUser.length < 2 || ['null', 'undefined', 'none', 'no'].includes(cleanUser.toLowerCase()) || /^\d+$/.test(cleanUser)) {
      return null;
    }

    if (/^[a-zA-Z0-9._]+$/.test(cleanUser)) {
      return { type: 'instagram', url: `https://www.instagram.com/${cleanUser}/`, label: 'Instagram' };
    }

    return null;
  }

  // Backward-compatible alias
  function ensureInstagram(handle) {
    const res = detectSocialOrWeb(handle);
    return res ? res.url : null;
  }

  // -- Escape helpers --------------------------------------------
  function esc(str) {
    return String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }
  function escAttr(str) {
    return String(str || '').replace(/"/g,'&quot;');
  }

  // -- Build distributor card ------------------------------------
  function buildCard(d, regionList, userLat, userLng, globalIndex) {
    const color  = getRegionColor(d.region, regionList);
    const mapQ   = encodeURIComponent([d.direccion, d.ciudad, d.region, 'Chile'].filter(Boolean).join(', '));
    const web    = ensureUrl(d.sitio_web);
    const social = detectSocialOrWeb(d.instagram);

    // Distance badge (exact road route or estimated road distance)
    let distanceBadge = '';
    if (userLat != null && userLng != null && d.lat != null && d.lng != null && !isNaN(d.lat) && !isNaN(d.lng)) {
      if (d._realDistance != null && !isNaN(d._realDistance)) {
        distanceBadge = `<span class="distributor-card__distance is-exact-route" title="Distancia y tiempo real por carretera">${formatExactRouteBadge(d._realDistance, d._realDuration)}</span>`;
      } else {
        const straightDist = d._distance ?? haversine(userLat, userLng, d.lat, d.lng);
        if (!isNaN(straightDist) && isFinite(straightDist)) {
          const roadDist = d._roadDistance ?? estimateRoadDistance(straightDist);
          const formatted = formatRoadDistance(roadDist);
          if (formatted) {
            distanceBadge = `<span class="distributor-card__distance" title="Distancia vial estimada">${ICON.target} <span>${formatted}</span></span>`;
          }
        }
      }
    }

    // Info rows
    let rows = '';
    if (d.direccion || d.ciudad) {
      const loc = [d.direccion, d.ciudad].filter(Boolean).join(', ');
      rows += `<div class="distributor-card__row">${ICON.pin}<span>${esc(loc)}</span></div>`;
    }
    if (d.telefono) {
      const telClean = d.telefono.replace(/\s/g, '');
      rows += `<div class="distributor-card__row">${ICON.phone}<a href="tel:${esc(telClean)}" style="color:inherit;text-decoration:none;">${esc(d.telefono)}</a></div>`;
    }
    if (d.horario) {
      rows += `<div class="distributor-card__row">${ICON.clock}<span>${esc(d.horario)}</span></div>`;
    }

    // Link buttons
    let links = '';
    if (d.lat != null && d.lng != null && !isNaN(d.lat) && !isNaN(d.lng)) {
      links += `<button type="button" class="distributor-card__link distributor-card__link--primary dloc-focus-map" data-global-index="${globalIndex}">${ICON.pin} Ver en mapa</button>`;
    } else if (mapQ) {
      links += `<a href="https://www.google.com/maps/search/?api=1&query=${mapQ}" target="_blank" rel="noopener" class="distributor-card__link distributor-card__link--primary">${ICON.maplink} Ver en mapa</a>`;
    }

    if (web) {
      links += `<a href="${esc(web)}" target="_blank" rel="noopener" class="distributor-card__link">${ICON.globe} Web</a>`;
    }

    if (social) {
      if (social.type === 'instagram') {
        links += `<a href="${esc(social.url)}" target="_blank" rel="noopener" class="distributor-card__link">${ICON.instagram} Instagram</a>`;
      } else if (social.type === 'web') {
        const cleanSocialUrl = social.url.toLowerCase().replace(/^https?:\/\/(?:www\.)?/, '').replace(/\/$/, '');
        const cleanWebUrl = (web || '').toLowerCase().replace(/^https?:\/\/(?:www\.)?/, '').replace(/\/$/, '');
        if (!web || cleanSocialUrl !== cleanWebUrl) {
          links += `<a href="${esc(social.url)}" target="_blank" rel="noopener" class="distributor-card__link">${ICON.globe} ${esc(social.label || 'Web')}</a>`;
        }
      }
    }

    return `
<article class="distributor-card"
  id="dloc-card-${globalIndex}"
  data-global-index="${globalIndex}"
  data-name="${escAttr(d.nombre)}"
  data-region="${escAttr(d.region)}"
  data-city="${escAttr(d.ciudad)}"
  data-address="${escAttr(d.direccion)}"
  data-lat="${d.lat ?? ''}"
  data-lng="${d.lng ?? ''}">
  <div class="distributor-card__header">
    <span class="distributor-card__region-badge" style="background:${color}">${esc((d.region || 'Sin región').replace(/^REGI[ÓO]N\s+(DE\s+LA\s+|DEL\s+|DE\s+)/i, ''))}</span>
    ${distanceBadge}
  </div>
  <h3 class="distributor-card__name">${esc(d.nombre)}</h3>
  <div class="distributor-card__info">${rows}</div>
  ${links ? `<div class="distributor-card__links">${links}</div>` : ''}
</article>`;
  }

  // -- Build Popup Content for Leaflet Map -----------------------
  function buildPopupContent(d) {
    const mapQ   = encodeURIComponent([d.direccion, d.ciudad, d.region, 'Chile'].filter(Boolean).join(', '));
    const web    = ensureUrl(d.sitio_web);
    const social = detectSocialOrWeb(d.instagram);

    let infoHtml = '';
    if (d._realDistance != null && !isNaN(d._realDistance)) {
      infoHtml += `<div class="dloc-popup__row dloc-popup__row--distance">${formatExactRouteBadge(d._realDistance, d._realDuration)}</div>`;
    }
    if (d.direccion || d.ciudad) {
      infoHtml += `<div class="dloc-popup__row">${ICON.pin} <span>${esc(d.direccion || '')}${d.ciudad ? ', ' + esc(d.ciudad) : ''}</span></div>`;
    }
    if (d.telefono) {
      infoHtml += `<div class="dloc-popup__row">${ICON.phone} <a href="tel:${esc(d.telefono.replace(/\s/g,''))}">${esc(d.telefono)}</a></div>`;
    }
    if (d.horario) {
      infoHtml += `<div class="dloc-popup__row">${ICON.clock} <span>${esc(d.horario)}</span></div>`;
    }

    let actionsHtml = `<a href="https://www.google.com/maps/dir/?api=1&destination=${d.lat},${d.lng}&travelmode=driving" target="_blank" rel="noopener" class="dloc-popup__btn dloc-popup__btn--primary">${ICON.maplink} <span>Cómo llegar</span></a>`;
    if (social && social.type === 'instagram') {
      actionsHtml += `<a href="${esc(social.url)}" target="_blank" rel="noopener" class="dloc-popup__btn dloc-popup__btn--instagram">${ICON.instagram} <span>Instagram</span></a>`;
    } else if (web) {
      actionsHtml += `<a href="${esc(web)}" target="_blank" rel="noopener" class="dloc-popup__btn dloc-popup__btn--web">${ICON.globe} <span>Web</span></a>`;
    } else if (social && social.type === 'web') {
      actionsHtml += `<a href="${esc(social.url)}" target="_blank" rel="noopener" class="dloc-popup__btn dloc-popup__btn--web">${ICON.globe} <span>${esc(social.label || 'Web')}</span></a>`;
    }

    return `
      <div class="dloc-leaflet-popup">
        <span class="dloc-popup__badge">${esc(d.region || 'Distribuidor')}</span>
        <h4 class="dloc-popup__title">${esc(d.nombre)}</h4>
        <div class="dloc-popup__body">${infoHtml}</div>
        <div class="dloc-popup__actions">${actionsHtml}</div>
      </div>
    `;
  }

  // -- Generate Pagination Page Numbers -------------------------
  function getPaginationRange(current, total) {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 4) {
      return [1, 2, 3, 4, 5, '...', total];
    }
    if (current >= total - 3) {
      return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, '...', current - 1, current, current + 1, '...', total];
  }

  // -- Google Sheets and CSV helpers ----------------------------
  function normalizeGoogleSheetUrl(rawUrl) {
    if (!rawUrl) return null;
    let url = rawUrl.trim();
    if (!url) return null;

    // Already a direct CSV export link
    if (url.includes('output=csv') || url.includes('format=csv') || url.includes('out:csv')) {
      return url;
    }

    // Google Sheets published web link: /pubhtml -> /pub?output=csv
    if (url.includes('/pubhtml')) {
      return url.replace(/\/pubhtml(\?.*)?$/, '/pub?output=csv');
    }
    if (url.includes('/pub') && !url.includes('output=csv')) {
      return url.includes('?') ? `${url}&output=csv` : `${url}?output=csv`;
    }

    // Standard sheet edit or share URL: https://docs.google.com/spreadsheets/d/{ID}/edit#gid={GID}
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (match && match[1]) {
      const sheetId = match[1];
      const gidMatch = url.match(/[#&?]gid=([0-9]+)/);
      const gidParam = gidMatch ? `&gid=${gidMatch[1]}` : '';
      return `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv${gidParam}`;
    }

    return url;
  }

  function parseCSV(text) {
    const lines = [];
    let row = [];
    let inQuotes = false;
    let current = '';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const next = text[i + 1];

      if (char === '"') {
        if (inQuotes && next === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if ((char === '\r' || char === '\n') && !inQuotes) {
        if (char === '\r' && next === '\n') i++;
        row.push(current.trim());
        if (row.length > 1 || (row.length === 1 && row[0] !== '')) {
          lines.push(row);
        }
        row = [];
        current = '';
      } else if (char === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    if (current || row.length > 0) {
      row.push(current.trim());
      lines.push(row);
    }
    return lines;
  }

  function normalizeHeaderKey(header) {
    if (!header) return '';
    return header.toString().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  const CITY_COORDS = {
    // Comunas Gran Santiago (Región Metropolitana)
    'pudahuel': { lat: -33.4485, lng: -70.7485 },
    'maipu': { lat: -33.5040, lng: -70.7620 },
    'cerrillos': { lat: -33.4950, lng: -70.7180 },
    'estacion central': { lat: -33.4610, lng: -70.7020 },
    'quinta normal': { lat: -33.4350, lng: -70.6950 },
    'lo prado': { lat: -33.4440, lng: -70.7220 },
    'cerro navia': { lat: -33.4250, lng: -70.7380 },
    'renca': { lat: -33.4050, lng: -70.7120 },
    'quilicura': { lat: -33.3640, lng: -70.7310 },
    'conchali': { lat: -33.3850, lng: -70.6750 },
    'huechuraba': { lat: -33.3750, lng: -70.6400 },
    'recoleta': { lat: -33.4100, lng: -70.6450 },
    'independencia': { lat: -33.4180, lng: -70.6650 },
    'santiago': { lat: -33.4489, lng: -70.6693 },
    'santiago centro': { lat: -33.4489, lng: -70.6693 },
    'providencia': { lat: -33.4310, lng: -70.6150 },
    'las condes': { lat: -33.4120, lng: -70.5650 },
    'vitacura': { lat: -33.3880, lng: -70.5750 },
    'lo barnechea': { lat: -33.3550, lng: -70.5150 },
    'la reina': { lat: -33.4450, lng: -70.5450 },
    'nunoa': { lat: -33.4550, lng: -70.6000 },
    'macul': { lat: -33.4850, lng: -70.6000 },
    'penalolen': { lat: -33.4850, lng: -70.5450 },
    'san joaquin': { lat: -33.4950, lng: -70.6300 },
    'san miguel': { lat: -33.4950, lng: -70.6550 },
    'pedro aguirre cerda': { lat: -33.4880, lng: -70.6780 },
    'lo espejo': { lat: -33.5200, lng: -70.6900 },
    'la cisterna': { lat: -33.5280, lng: -70.6650 },
    'san ramon': { lat: -33.5350, lng: -70.6450 },
    'la granja': { lat: -33.5350, lng: -70.6250 },
    'el bosque': { lat: -33.5600, lng: -70.6650 },
    'la pintana': { lat: -33.5850, lng: -70.6350 },
    'san bernardo': { lat: -33.5950, lng: -70.7050 },
    'puente alto': { lat: -33.6150, lng: -70.5750 },
    'pirque': { lat: -33.6350, lng: -70.5750 },
    'buin': { lat: -33.7320, lng: -70.7420 },
    'paine': { lat: -33.8150, lng: -70.7450 },
    'talagante': { lat: -33.6650, lng: -70.9250 },
    'penaflor': { lat: -33.6100, lng: -70.8850 },
    'padre hurtado': { lat: -33.5650, lng: -70.8150 },
    'melipilla': { lat: -33.6850, lng: -71.2150 },
    'colina': { lat: -33.2050, lng: -70.6750 },
    'lampa': { lat: -33.2850, lng: -70.8750 },
    'tiltil': { lat: -33.0850, lng: -70.9250 },
    'chicureo': { lat: -33.2750, lng: -70.6600 },

    // Regiones y ciudades principales
    'temuco': { lat: -38.7359, lng: -72.5904 },
    'padre las casas': { lat: -38.7619, lng: -72.5978 },
    'villarrica': { lat: -39.2844, lng: -72.2272 },
    'pucon': { lat: -39.2789, lng: -71.9753 },
    'angol': { lat: -37.7981, lng: -72.7161 },
    'valdivia': { lat: -39.8196, lng: -73.2452 },
    'la union': { lat: -40.2922, lng: -73.0819 },
    'osorno': { lat: -40.5739, lng: -73.1335 },
    'puerto montt': { lat: -41.4693, lng: -72.9424 },
    'puerto varas': { lat: -41.3195, lng: -72.9854 },
    'castro': { lat: -42.4722, lng: -73.7731 },
    'ancud': { lat: -41.8689, lng: -73.8278 },
    'coyhaique': { lat: -45.5712, lng: -72.0685 },
    'punta arenas': { lat: -53.1638, lng: -70.9171 },
    'concepcion': { lat: -36.8270, lng: -73.0503 },
    'talcahuano': { lat: -36.7249, lng: -73.1168 },
    'san pedro de la paz': { lat: -36.8406, lng: -73.1044 },
    'chiguayante': { lat: -36.9178, lng: -73.0239 },
    'coronel': { lat: -37.0306, lng: -73.1408 },
    'los angeles': { lat: -37.4697, lng: -72.3537 },
    'chillan': { lat: -36.6066, lng: -72.1034 },
    'talca': { lat: -35.4264, lng: -71.6554 },
    'curico': { lat: -34.9854, lng: -71.2394 },
    'linares': { lat: -35.8456, lng: -71.5975 },
    'rancagua': { lat: -34.1708, lng: -70.7444 },
    'machali': { lat: -34.1800, lng: -70.6500 },
    'san fernando': { lat: -34.5838, lng: -70.9889 },
    'codegua': { lat: -34.0333, lng: -70.6500 },
    'graneros': { lat: -34.0667, lng: -70.7167 },
    'san francisco de mostazal': { lat: -34.0200, lng: -70.7100 },
    'mostazal': { lat: -34.0200, lng: -70.7100 },
    'valparaiso': { lat: -33.0472, lng: -71.6127 },
    'vina del mar': { lat: -33.0245, lng: -71.5518 },
    'quilpue': { lat: -33.0494, lng: -71.4428 },
    'villa alemana': { lat: -33.0425, lng: -71.3736 },
    'quillota': { lat: -32.8808, lng: -71.2483 },
    'la serena': { lat: -29.9027, lng: -71.2519 },
    'coquimbo': { lat: -29.9533, lng: -71.3436 },
    'ovalle': { lat: -30.5983, lng: -71.2003 },
    'copiapo': { lat: -27.3668, lng: -70.3323 },
    'antofagasta': { lat: -23.6509, lng: -70.3975 },
    'calama': { lat: -22.4544, lng: -68.9294 },
    'iquique': { lat: -20.2167, lng: -70.1444 },
    'arica': { lat: -18.4783, lng: -70.3126 }
  };

  function isGenericCoordinates(lat, lng) {
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return true;
    // Santiago Centro centroid: -33.4569, -70.6483 or -33.4489, -70.6693
    const isStgoCentroid = Math.abs(lat - (-33.4569)) < 0.005 && Math.abs(lng - (-70.6483)) < 0.005;
    const isOldCentroid  = Math.abs(lat - (-33.4489)) < 0.005 && Math.abs(lng - (-70.6693)) < 0.005;
    return isStgoCentroid || isOldCentroid;
  }

  function parseCoordinate(val) {
    if (val == null) return null;
    let s = String(val).trim();
    if (!s) return null;

    // Handle formats like -71,041,282 or -70,648,205 (Spanish Google Sheet thousands format for decimals)
    if (s.includes(',') && !s.includes('.')) {
      const parts = s.split(',');
      if (parts.length > 1) {
        s = parts[0] + '.' + parts.slice(1).join('');
      }
    } else if (s.includes(',') && s.includes('.')) {
      s = s.replace(/,/g, '');
    }

    const num = parseFloat(s);
    return isNaN(num) ? null : num;
  }

  function csvToDistributors(csvText) {
    const rows = parseCSV(csvText);
    if (rows.length < 2) return [];

    const rawHeaders = rows[0];
    const headers = rawHeaders.map(normalizeHeaderKey);

    return rows.slice(1).map(row => {
      const d = {};

      // 1. Pass: Exact match on standard headers
      headers.forEach((h, idx) => {
        const val = (row[idx] || '').trim();
        if (!h || !val) return;

        if (h === 'direccion' || h === 'calle' || h === 'dir') {
          d.direccion = val;
        } else if (h === 'nombre' || h === 'tienda' || h === 'distribuidor' || h === 'local') {
          d.nombre = val;
        } else if (h === 'comuna') {
          d.comuna = val;
        } else if (h === 'ciudad') {
          d.ciudad = val;
        } else if (h === 'region') {
          d.region = val;
        } else if (h === 'telefono' || h === 'fono' || h === 'celular' || h === 'whatsapp' || h === 'tel') {
          d.telefono = val;
        } else if (h === 'sitio_web' || h === 'web' || h === 'url' || h === 'pagina_web' || h === 'link') {
          d.sitio_web = val;
        } else if (h === 'instagram' || h === 'ig' || h === 'insta' || h === 'rrss' || h === 'red_social' || h === 'redes_sociales' || h === 'redes' || h === 'redes_social') {
          d.instagram = val;
        } else if (h === 'email' || h === 'correo') {
          d.email = val;
        } else if (h === 'horario' || h === 'atencion') {
          d.horario = val;
        } else if (h === 'lat_validada' || h === 'latitud_validada' || h === 'lat_candidato' || h === 'lat' || h === 'latitud') {
          const num = parseCoordinate(val);
          if (num != null) d.lat = num;
        } else if (h === 'lng_validada' || h === 'longitud_validada' || h === 'lng_candidato' || h === 'lng' || h === 'lon' || h === 'longitud' || h === 'ing') {
          const num = parseCoordinate(val);
          if (num != null) d.lng = num;
        }
      });

      // 2. Pass: Fallback match ONLY for properties not yet found (and explicitly ignore 'direccion_encontrada')
      headers.forEach((h, idx) => {
        const val = (row[idx] || '').trim();
        if (!h || !val) return;

        if (!d.nombre && (h.includes('nombre') || h.includes('tienda') || h.includes('distribuidor'))) {
          d.nombre = val;
        } else if (!d.direccion && h.includes('direccion') && !h.includes('encontrada') && !h.includes('google') && !h.includes('geocode') && !h.includes('normalizada')) {
          d.direccion = val;
        } else if (!d.comuna && h.includes('comuna')) {
          d.comuna = val;
        } else if (!d.ciudad && h.includes('ciudad')) {
          d.ciudad = val;
        } else if (!d.region && h.includes('region')) {
          d.region = val;
        } else if (!d.telefono && (h.includes('telefono') || h.includes('fono') || h.includes('celular') || h.includes('whatsapp') || h.startsWith('tel_') || h.startsWith('tel-'))) {
          d.telefono = val;
        } else if (!d.sitio_web && (h.includes('sitio_web') || h.includes('pagina_web') || h.startsWith('web') || h.startsWith('url'))) {
          d.sitio_web = val;
        } else if (!d.instagram && (h.includes('instagram') || h.startsWith('insta') || h.includes('redes') || h.includes('red_social') || h.includes('rrss') || h.startsWith('ig_') || h.startsWith('ig-') || h.endsWith('_ig'))) {
          d.instagram = val;
        } else if (!d.email && (h.includes('email') || h.includes('correo'))) {
          d.email = val;
        } else if (!d.horario && (h.includes('horario') || h.includes('atencion'))) {
          d.horario = val;
        } else if (d.lat == null && (h.includes('lat_validada') || h.includes('lat_candidato') || h.startsWith('lat') || h.includes('latitud'))) {
          const num = parseCoordinate(val);
          if (num != null) d.lat = num;
        } else if (d.lng == null && (h.includes('lng_validada') || h.includes('lng_candidato') || h.startsWith('lng') || h.startsWith('lon') || h.includes('longitud'))) {
          const num = parseCoordinate(val);
          if (num != null) d.lng = num;
        }
      });

      // Normalización inteligente y exhaustiva de regiones de Chile
      if (d.region) {
        const regClean = d.region.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
        if (regClean.includes('ARAUCA') || regClean === 'IX' || regClean.includes('IX REGION')) {
          d.region = 'REGIÓN DE LA ARAUCANÍA';
        } else if (regClean.includes('BIOBIO') || regClean.includes('BIO BIO') || regClean === 'VIII' || regClean.includes('VIII REGION')) {
          d.region = 'REGIÓN DEL BIOBÍO';
        } else if (regClean.includes('NUBLE') || regClean === 'XVI' || regClean.includes('XVI REGION')) {
          d.region = 'REGIÓN DE ÑUBLE';
        } else if (regClean.includes('MAULE') || regClean === 'VII' || regClean.includes('VII REGION')) {
          d.region = 'REGIÓN DEL MAULE';
        } else if (regClean.includes('HIGGINS') || regClean.includes('O\'HIGGINS') || regClean === 'VI' || regClean.includes('VI REGION')) {
          d.region = 'REGIÓN DE O\'HIGGINS';
        } else if (regClean.includes('METROPOLITANA') || regClean === 'RM' || regClean.includes('SANTIAGO')) {
          d.region = 'REGIÓN METROPOLITANA';
        } else if (regClean.includes('COQUIMBO') || regClean === 'IV' || regClean.includes('IV REGION')) {
          d.region = 'REGIÓN DE COQUIMBO';
        } else if (regClean.includes('ATACAM') || regClean.includes('ATACAN') || regClean === 'III' || regClean.includes('III REGION')) {
          d.region = 'REGIÓN DE ATACAMA';
        } else if (regClean.includes('VALPARAISO') || regClean.includes('V REGION') || regClean === 'V') {
          d.region = 'REGIÓN DE VALPARAÍSO';
        } else if (regClean.includes('LOS RIOS') || regClean === 'XIV' || regClean.includes('XIV REGION')) {
          d.region = 'REGIÓN DE LOS RÍOS';
        } else if (regClean.includes('LOS LAGOS') || regClean === 'X' || regClean.includes('X REGION')) {
          d.region = 'REGIÓN DE LOS LAGOS';
        } else if (regClean.includes('AYSEN') || regClean.includes('AISEN') || regClean === 'XI' || regClean.includes('XI REGION')) {
          d.region = 'REGIÓN DE AYSÉN';
        } else if (regClean.includes('MAGALLANES') || regClean === 'XII' || regClean.includes('XII REGION')) {
          d.region = 'REGIÓN DE MAGALLANES';
        } else if (regClean.includes('ANTOFAGASTA') || regClean === 'II' || regClean.includes('II REGION')) {
          d.region = 'REGIÓN DE ANTOFAGASTA';
        } else if (regClean.includes('TARAPACA') || regClean === 'I' || regClean.includes('I REGION')) {
          d.region = 'REGIÓN DE TARAPACÁ';
        } else if (regClean.includes('ARICA') || regClean.includes('PARINACOTA') || regClean === 'XV' || regClean.includes('XV REGION')) {
          d.region = 'REGIÓN DE ARICA Y PARINACOTA';
        } else if (regClean.length < 3) {
          d.region = '';
        }
      }

      // Auto-geolocalización de respaldo si no se ingresó latitud o longitud en Google Sheets, o si son genéricas del centro
      if (d.lat == null || d.lng == null || isNaN(d.lat) || isNaN(d.lng) || isGenericCoordinates(d.lat, d.lng)) {
        const fullText = `${d.direccion} ${d.ciudad} ${d.comuna} ${d.region}`.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        let matched = false;
        for (const [cityName, coords] of Object.entries(CITY_COORDS)) {
          if (cityName !== 'santiago' && cityName !== 'santiago centro' && fullText.includes(cityName)) {
            d.lat = coords.lat;
            d.lng = coords.lng;
            matched = true;
            break;
          }
        }
        if (!matched && (d.lat == null || d.lng == null || isNaN(d.lat) || isNaN(d.lng))) {
          if (fullText.includes('santiago')) {
            d.lat = CITY_COORDS['santiago'].lat;
            d.lng = CITY_COORDS['santiago'].lng;
          }
        }
      }

      return d;
    }).filter(d => d.nombre && d.nombre.trim());
  }

  async function fetchDistributorData(jsonUrl, sheetUrl, dataSource, cacheMins) {
    const normSheetUrl = normalizeGoogleSheetUrl(sheetUrl);
    const isDesignMode = typeof window.Shopify !== 'undefined' && window.Shopify.designMode;
    const cacheKey = 'dloc_sheet_cache_' + (normSheetUrl ? btoa(normSheetUrl).slice(0, 32) : 'local');

    // 1. Try Cache if enabled, not in designMode, and has valid coordinates
    if (!isDesignMode && cacheMins > 0 && normSheetUrl) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const ageMinutes = (Date.now() - parsed.timestamp) / (1000 * 60);
          const hasValidCoords = parsed.data && parsed.data.some(d => d.lat != null && !isNaN(d.lat) && d.lat < -15);
          if (hasValidCoords && ageMinutes < cacheMins && Array.isArray(parsed.data) && parsed.data.length > 0) {
            return parsed.data;
          }
        }
      } catch (e) {
        // Ignore localStorage error
      }
    }

    // 2. Fetch from Google Sheets if configured
    if ((dataSource === 'auto' || dataSource === 'sheets') && normSheetUrl) {
      try {
        const res = await fetch(normSheetUrl, { cache: isDesignMode ? 'no-cache' : 'default' });
        if (res.ok) {
          const text = await res.text();
          if (!text.trim().startsWith('<!DOCTYPE') && !text.trim().startsWith('<html')) {
            const data = csvToDistributors(text);
            if (data.length > 0) {
              if (!isDesignMode && cacheMins > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data }));
                } catch (e) {}
              }
              return data;
            }
          }
        }
        console.warn('[DistributorLocator] Google Sheets fetch failed or returned empty. Falling back if allowed.');
      } catch (sheetErr) {
        console.warn('[DistributorLocator] Google Sheets network error:', sheetErr);
      }
    }

    // 3. Fallback to local JSON if allowed
    if (dataSource === 'auto' || dataSource === 'json') {
      const res = await fetch(jsonUrl);
      if (!res.ok) throw new Error(`HTTP error ${res.status} fetching local JSON`);
      const data = await res.json();
      return (data || []).filter(d => d.nombre && d.nombre.trim());
    }

    return [];
  }

  // -- Main initialiser ------------------------------------------
  function init(section) {
    const sectionId    = section.dataset.sectionId;
    const jsonUrl      = section.dataset.jsonUrl;
    const sheetUrl     = section.dataset.sheetUrl || '';
    const dataSource   = section.dataset.dataSource || 'auto';
    const cacheMins    = parseInt(section.dataset.cacheMins || '15', 10);
    const radiusKm     = parseInt(section.dataset.radius || DEFAULT_RADIUS_KM, 10);
    const itemsPerPage = parseInt(section.dataset.perPage || DEFAULT_PER_PAGE, 10);

    const searchInput   = section.querySelector('.distributor-locator__search');
    const searchClear   = section.querySelector('.distributor-locator__search-clear');
    const suggestionsEl = section.querySelector(`#dloc-suggestions-${sectionId}`);
    const regionSelect  = section.querySelector('.distributor-locator__region-select');
    const grid          = section.querySelector(`#dloc-grid-${sectionId}`);
    const paginationEl  = section.querySelector(`#dloc-pagination-${sectionId}`);
    const emptyState    = section.querySelector(`#dloc-empty-${sectionId}`);
    const countEl       = section.querySelector('.distributor-locator__count');
    const skeleton      = section.querySelector('.distributor-locator__skeleton-grid');
    const mapEl         = section.querySelector(`#dloc-openmap-${sectionId}`);
    const geoBanner     = section.querySelector('.distributor-locator__geo-banner');
    const geoBannerText = section.querySelector('.distributor-locator__geo-banner-text');
    const geoShowAll    = section.querySelector('.distributor-locator__geo-show-all');
    const geoLocate     = section.querySelector('.distributor-locator__geo-locate');
    const geoBannerClose = section.querySelector('.distributor-locator__geo-banner-close');
    const tabButtons    = section.querySelectorAll('.distributor-locator__tab-btn');
    const cardsPane     = section.querySelector('.distributor-locator__cards-pane');
    const mapPane       = section.querySelector('.distributor-locator__map-pane');

    const mapProvider   = section.dataset.mapProvider || (section.dataset.googleApiKey ? 'google' : 'osm');
    const googleApiKey  = section.dataset.googleApiKey || '';

    let allData         = [];
    let regionList      = [];
    let userLocation    = null;   // { lat, lng, city?, source }
    let isNearbyMode    = false;
    let activeMapType   = 'none'; // 'google' | 'leaflet'
    let mapObj          = null;   // Leaflet map
    let googleMap       = null;   // Google Map
    let googleInfoWindow = null;  // Google InfoWindow
    let googleMarkers   = [];     // Google Markers
    let googleUserMarker = null;  // Google User Marker
    let googleRadiusCircle = null;// Google Radius Circle
    let markersLayer    = null;   // Leaflet featureGroup
    let userMarker      = null;   // Leaflet user marker
    let radiusCircle    = null;   // Leaflet radius circle
    let markersMap      = new Map(); // globalIndex -> Marker (Leaflet or Google)
    let searchTerm      = '';
    let activeRegion    = '';
    let renderTimer     = null;
    let filteredData    = [];
    let currentPage     = 1;
    const routeCache    = new Map(); // key -> { dist: meters, dur: seconds }

    function getRouteCacheKey(userLoc, destLat, destLng) {
      return `${Number(userLoc.lat).toFixed(3)},${Number(userLoc.lng).toFixed(3)}_${Number(destLat).toFixed(3)},${Number(destLng).toFixed(3)}`;
    }

    function applyRouteToCard(globalIdx, distMeters, durSec) {
      const card = document.getElementById(`dloc-card-${globalIdx}`);
      if (!card) return;
      const distBadge = card.querySelector('.distributor-card__distance');
      if (distBadge) {
        distBadge.innerHTML = formatExactRouteBadge(distMeters, durSec);
        distBadge.setAttribute('title', 'Distancia y tiempo real por carretera');
        distBadge.classList.add('is-exact-route');
      }
    }

    async function enrichVisibleCardsWithRoutes(pageData, startIndex) {
      if (!userLocation || userLocation.lat == null || userLocation.lng == null) return;

      const uncached = [];

      pageData.forEach((d, i) => {
        if (d.lat == null || d.lng == null || isNaN(d.lat) || isNaN(d.lng)) return;
        const globalIdx = startIndex + i;
        const cacheKey = getRouteCacheKey(userLocation, d.lat, d.lng);

        if (routeCache.has(cacheKey)) {
          const cached = routeCache.get(cacheKey);
          d._realDistance = cached.dist;
          d._realDuration = cached.dur;
          applyRouteToCard(globalIdx, cached.dist, cached.dur);
        } else {
          uncached.push({ d, globalIdx, cacheKey });
        }
      });

      if (!uncached.length) return;

      // Real road distance & driving duration (OSRM Chilean Network Table Service)
      fetchOSRMTable(uncached);
    }

    async function fetchOSRMTable(items) {
      if (!items || !items.length || !userLocation) return;
      try {
        const coords = [`${Number(userLocation.lng).toFixed(5)},${Number(userLocation.lat).toFixed(5)}`];
        items.forEach(it => {
          coords.push(`${Number(it.d.lng).toFixed(5)},${Number(it.d.lat).toFixed(5)}`);
        });

        const url = `https://router.project-osrm.org/table/v1/driving/${coords.join(';')}?sources=0&annotations=distance,duration`;
        const res = await fetch(url, { signal: AbortSignal.timeout(6000) });
        if (!res.ok) return;
        const json = await res.json();

        if (json.code === 'Ok' && json.distances && json.distances[0]) {
          items.forEach((it, idx) => {
            const distMeters = json.distances[0][idx + 1];
            const durSec = json.durations && json.durations[0] ? json.durations[0][idx + 1] : null;

            if (distMeters != null && !isNaN(distMeters)) {
              routeCache.set(it.cacheKey, { dist: distMeters, dur: durSec });
              it.d._realDistance = distMeters;
              it.d._realDuration = durSec;
              applyRouteToCard(it.globalIdx, distMeters, durSec);
            }
          });
        }
      } catch (err) {
        console.warn('[DistributorLocator] OSRM routing network error:', err);
      }
    }

    // Helper to normalize strings for search (lowercase and remove accents)
    function cleanStr(s) {
      return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    }

    function geocodeMissingDistributors(distributors) {
      if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.Geocoder) return;
      const needGeocode = distributors.filter(d => (d.lat == null || d.lng == null || isGenericCoordinates(d.lat, d.lng)) && d.direccion && (d.comuna || d.ciudad));
      if (!needGeocode.length) return;

      const geocoder = new google.maps.Geocoder();
      const CACHE_KEY_PREFIX = 'dloc_geo_cache_';

      needGeocode.slice(0, 8).forEach((d, i) => {
        const query = `${d.direccion}, ${d.comuna || d.ciudad || ''}, Chile`;
        const cacheKey = CACHE_KEY_PREFIX + cleanStr(query);
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            d.lat = parsed.lat;
            d.lng = parsed.lng;
            return;
          } catch {}
        }

        setTimeout(() => {
          geocoder.geocode({ address: query, componentRestrictions: { country: 'CL' } }, (results, status) => {
            if (status === 'OK' && results && results[0] && results[0].geometry) {
              const loc = results[0].geometry.location;
              d.lat = loc.lat();
              d.lng = loc.lng();
              try {
                localStorage.setItem(cacheKey, JSON.stringify({ lat: d.lat, lng: d.lng }));
              } catch {}
            }
          });
        }, i * 350);
      });
    }

    // --- Load Data (Google Sheets / JSON) ---------------------
    fetchDistributorData(jsonUrl, sheetUrl, dataSource, cacheMins)
      .then(async data => {
        allData = data.filter(d => d.nombre && d.nombre.trim());
        if (typeof window.google !== 'undefined' && window.google.maps) {
          geocodeMissingDistributors(allData);
        }
        regionList = [...new Set(allData.map(d => d.region).filter(r => r && r.trim().length > 3))].sort();

        if (regionSelect) {
          while (regionSelect.options.length > 1) {
            regionSelect.remove(1);
          }
          regionList.forEach(region => {
            const opt = document.createElement('option');
            opt.value = region;
            opt.textContent = region;
            regionSelect.appendChild(opt);
          });
        }

        if (skeleton) skeleton.remove();

        // -- Init Map (Google Maps or OpenStreetMap) --
        if (mapEl) {
          initMap(mapEl);
        }

        // -- Try geolocation --
        showGeoStatus('loading');
        userLocation = await detectLocation();

        if (userLocation) {
          // Filter nearby within 100km
          const nearby = filterByRadius(allData, userLocation.lat, userLocation.lng, radiusKm);
          if (nearby.length > 0) {
            isNearbyMode = true;
            showGeoStatus('found', userLocation, nearby.length, radiusKm);
            renderCards(nearby, true);
          } else {
            showGeoStatus('noneNearby', userLocation, 0, radiusKm);
            renderCards(allData, true);
          }
          updateUserLocationOnMap(userLocation, radiusKm);
        } else {
          showGeoStatus('denied');
          renderCards(allData, true);
        }
      })
      .catch(err => {
        console.error('[DistributorLocator]', err);
        if (skeleton) skeleton.remove();
        showEmpty();
      });

    // --- Filter by radius -------------------------------------
    function filterByRadius(data, lat, lng, km) {
      return data
        .filter(d => d.lat != null && d.lng != null && !isNaN(d.lat) && !isNaN(d.lng))
        .map(d => {
          const straight = haversine(lat, lng, d.lat, d.lng);
          const road = estimateRoadDistance(straight);
          return { ...d, _distance: straight, _roadDistance: road };
        })
        .filter(d => d._distance <= km)
        .sort((a, b) => (a._roadDistance ?? a._distance) - (b._roadDistance ?? b._distance));
    }

    // --- Geo banner state machine -----------------------------
    function showGeoStatus(state, loc, count, km) {
      if (!geoBanner) return;

      geoBanner.hidden = false;
      geoBanner.className = 'distributor-locator__geo-banner distributor-locator__geo-banner--' + state;

      let foundMsg = `${ICON.locme} Mostrando <strong>${count}</strong> distribuidores dentro de <strong>${km} km</strong>${loc && loc.city ? ' de <strong>' + esc(loc.city) + '</strong>' : ' de tu ubicación'}.`;
      if (loc && loc.source === 'ip') {
        foundMsg = `${ICON.locme} Ubicación por red estimada en <strong>${esc(loc.city || 'Santiago')}</strong> (${count} tiendas en ${km} km). <span class="dloc-geo-hint">Puedes buscar tu comuna o mover el pin en el mapa para máxima precisión.</span>`;
      } else if (loc && (loc.source === 'places' || loc.source === 'manual' || loc.source === 'search')) {
        foundMsg = `${ICON.locme} Mostrando <strong>${count}</strong> distribuidores cerca de <strong>${esc(loc.city || 'tu ubicación')}</strong> (radio ${km} km).`;
      }

      const messages = {
        loading:    `${ICON.locme} Detectando tu ubicación...`,
        found:      foundMsg,
        noneNearby: `${ICON.warning} No hay distribuidores dentro de ${km} km de tu ubicación. Mostrando todos.`,
        denied:     `${ICON.locme} No se pudo detectar tu ubicación. Mostrando todos los distribuidores.`,
      };

      if (geoBannerText) geoBannerText.innerHTML = messages[state] || '';

      // Show/hide action buttons
      if (geoShowAll) geoShowAll.hidden  = state !== 'found';
      if (geoLocate)  geoLocate.hidden   = state === 'loading' || state === 'found' || state === 'noneNearby';
    }

    // --- Dynamic User Search Location (Map click, Place Autocomplete, or Search query) ---
    function setUserSearchLocation(loc) {
      if (!loc || loc.lat == null || loc.lng == null || isNaN(loc.lat) || isNaN(loc.lng)) return;
      userLocation = loc;
      isNearbyMode = true;

      const nearby = filterByRadius(allData, loc.lat, loc.lng, radiusKm);
      if (nearby.length > 0) {
        showGeoStatus('found', loc, nearby.length, radiusKm);
        renderCards(nearby, true);
      } else {
        showGeoStatus('noneNearby', loc, 0, radiusKm);
        renderCards(allData, true);
      }

      updateUserLocationOnMap(loc, radiusKm);

      if (activeMapType === 'google' && googleMap) {
        googleMap.panTo({ lat: loc.lat, lng: loc.lng });
        if (googleMap.getZoom() < 12) googleMap.setZoom(12);
      } else if (mapObj) {
        mapObj.panTo([loc.lat, loc.lng]);
        if (mapObj.getZoom() < 12) mapObj.setZoom(12);
      }
    }

    function geocodeAddress(query) {
      if (!query || !query.trim()) return;
      const clean = cleanStr(query);

      // 1. Check CITY_COORDS first
      for (const [cityName, coords] of Object.entries(CITY_COORDS)) {
        if (clean === cityName || clean.includes(cityName)) {
          setUserSearchLocation({
            lat: coords.lat,
            lng: coords.lng,
            city: cityName.toUpperCase(),
            source: 'search'
          });
          return;
        }
      }

      // 2. Google Maps Geocoder if active
      if (typeof window.google !== 'undefined' && window.google.maps && window.google.maps.Geocoder) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: query, componentRestrictions: { country: 'CL' } }, (results, status) => {
          if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
            const loc = results[0].geometry.location;
            setUserSearchLocation({
              lat: loc.lat(),
              lng: loc.lng(),
              city: results[0].formatted_address || query,
              source: 'search'
            });
          }
        });
      }
    }

    // --- Render cards with pagination -------------------------
    function renderCards(data, resetPage = false) {
      filteredData = data;
      if (resetPage) {
        currentPage = 1;
      }

      grid.innerHTML = '';

      if (!filteredData.length) {
        showEmpty();
        if (countEl) countEl.textContent = '';
        if (paginationEl) {
          paginationEl.hidden = true;
          paginationEl.innerHTML = '';
        }
        updateMapMarkers([]);
        return;
      }

      hideEmpty();

      const totalItems = filteredData.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);

      if (currentPage > totalPages) {
        currentPage = totalPages;
      }
      if (currentPage < 1) {
        currentPage = 1;
      }

      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex   = Math.min(startIndex + itemsPerPage, totalItems);
      const pageData   = filteredData.slice(startIndex, endIndex);

      if (countEl) {
        countEl.innerHTML = `<strong>${totalItems}</strong> distribuidor${totalItems !== 1 ? 'es' : ''} encontrado${totalItems !== 1 ? 's' : ''}`;
      }

      // Render cards for current page
      const frag = document.createDocumentFragment();
      pageData.forEach((d, i) => {
        const globalIdx = startIndex + i;
        const tmp = document.createElement('div');
        tmp.innerHTML = buildCard(d, regionList, userLocation?.lat, userLocation?.lng, globalIdx);
        const card = tmp.firstElementChild;
        card.style.animationDelay = Math.min(i * 18, 250) + 'ms';

        // Click on card anywhere to zoom and focus on map
        card.addEventListener('click', (e) => {
          if (e.target.closest('a') || e.target.closest('.dloc-focus-map')) return;
          highlightMarker(globalIdx, true);
          highlightCard(globalIdx);
          if (window.innerWidth < 992) {
            activateTab('map');
          }
        });

        frag.appendChild(card);
      });
      grid.appendChild(frag);

      // Asynchronously fetch exact driving route distances & travel times for visible cards
      enrichVisibleCardsWithRoutes(pageData, startIndex);

      // Button "Ver en mapa" event
      grid.querySelectorAll('.dloc-focus-map').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const globalIdx = parseInt(btn.dataset.globalIndex, 10);
          highlightMarker(globalIdx, true);
          highlightCard(globalIdx);
          if (window.innerWidth < 992) {
            activateTab('map');
          }
        });
      });

      // Render Pagination Controls
      renderPagination(totalItems, totalPages, startIndex, endIndex);

      // Update Map with all filtered markers
      updateMapMarkers(filteredData);
    }

    // --- Render Pagination HTML & Event Listeners -------------
    function renderPagination(totalItems, totalPages, startIndex, endIndex) {
      if (!paginationEl) return;

      if (totalPages <= 1) {
        paginationEl.hidden = true;
        paginationEl.classList.add('is-hidden');
        paginationEl.style.display = 'none';
        paginationEl.innerHTML = '';
        return;
      }

      paginationEl.hidden = false;
      paginationEl.classList.remove('is-hidden');
      paginationEl.style.display = 'flex';

      const pageRange = getPaginationRange(currentPage, totalPages);
      let pageButtonsHtml = '';

      pageRange.forEach(p => {
        if (p === '...') {
          pageButtonsHtml += `<span class="dloc-page-ellipsis">...</span>`;
        } else {
          const isActive = p === currentPage;
          pageButtonsHtml += `
            <button
              type="button"
              class="dloc-page-btn dloc-page-btn--num${isActive ? ' is-active' : ''}"
              data-page="${p}"
              aria-label="Página ${p}"
              ${isActive ? 'aria-current="page"' : ''}
            >${p}</button>
          `;
        }
      });

      paginationEl.innerHTML = `
        <div class="dloc-pagination-summary">
          Mostrando <strong>${startIndex + 1}-${endIndex}</strong> de <strong>${totalItems}</strong> distribuidores
        </div>
        <div class="dloc-pagination-nav">
          <button
            type="button"
            class="dloc-page-btn dloc-page-btn--prev"
            data-page="${currentPage - 1}"
            ${currentPage === 1 ? 'disabled' : ''}
            aria-label="Página anterior"
          >
            ${ICON.chevronLeft} <span>Anterior</span>
          </button>

          <div class="dloc-pagination-numbers">
            ${pageButtonsHtml}
          </div>

          <button
            type="button"
            class="dloc-page-btn dloc-page-btn--next"
            data-page="${currentPage + 1}"
            ${currentPage === totalPages ? 'disabled' : ''}
            aria-label="Página siguiente"
          >
            <span>Siguiente</span> ${ICON.chevronRight}
          </button>
        </div>
      `;

      // Attach click events
      paginationEl.querySelectorAll('.dloc-page-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.disabled || !btn.dataset.page) return;
          const targetPage = parseInt(btn.dataset.page, 10);
          if (targetPage === currentPage || isNaN(targetPage)) return;

          currentPage = targetPage;
          renderCards(filteredData, false);

          // Smooth scroll to top of list
          const controlsEl = section.querySelector('.distributor-locator__controls');
          if (controlsEl) {
            controlsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });
    }

    function showEmpty() {
      if (emptyState) {
        emptyState.hidden = false;
        emptyState.classList.remove('is-hidden');
        emptyState.style.display = 'flex';
      }
    }
    function hideEmpty() {
      if (emptyState) {
        emptyState.hidden = true;
        emptyState.classList.add('is-hidden');
        emptyState.style.display = 'none';
      }
    }

    // --- Autocomplete Suggestions Logic -----------------------
    function renderSuggestions(query) {
      if (!suggestionsEl) return;
      const cleanQ = cleanStr(query);

      if (cleanQ.length < 3) {
        suggestionsEl.hidden = true;
        suggestionsEl.innerHTML = '';
        return;
      }

      // Group matching cities/communes
      const cityMatches = new Map();
      const storeMatches = [];

      allData.forEach(d => {
        const cityClean = cleanStr(d.ciudad);
        const comunaClean = cleanStr(d.comuna);
        const nameClean = cleanStr(d.nombre);

        // Check if city or commune matches
        const matchedCityName = (cityClean.includes(cleanQ) ? d.ciudad : null) ||
                               (comunaClean.includes(cleanQ) ? d.comuna : null);

        if (matchedCityName) {
          const key = cleanStr(matchedCityName);
          if (!cityMatches.has(key)) {
            cityMatches.set(key, {
              name: matchedCityName,
              region: d.region,
              count: 0,
              lat: d.lat,
              lng: d.lng
            });
          }
          const item = cityMatches.get(key);
          item.count++;
          if ((item.lat == null || isNaN(item.lat)) && d.lat != null && !isNaN(d.lat)) {
            item.lat = d.lat;
            item.lng = d.lng;
          }
        }

        // Check if store name matches directly
        if (nameClean.includes(cleanQ) && storeMatches.length < 5) {
          storeMatches.push(d);
        }
      });

      const citiesArray = Array.from(cityMatches.values()).slice(0, 5);

      if (citiesArray.length === 0 && storeMatches.length === 0) {
        suggestionsEl.hidden = true;
        suggestionsEl.innerHTML = '';
        return;
      }

      let html = '';

      if (citiesArray.length > 0) {
        html += `<div class="distributor-locator__suggestions-group">Ciudades y Comunas</div>`;
        citiesArray.forEach(c => {
          html += `
            <div class="distributor-locator__suggestion-item" data-type="city" data-value="${escAttr(c.name)}" data-lat="${c.lat ?? ''}" data-lng="${c.lng ?? ''}">
              <span class="distributor-locator__suggestion-icon">${ICON.pin}</span>
              <div class="distributor-locator__suggestion-text">
                <span class="distributor-locator__suggestion-title">${esc(c.name)}</span>
                <span class="distributor-locator__suggestion-sub">${esc(c.region || 'Chile')}</span>
              </div>
              <span class="distributor-locator__suggestion-count">${c.count} ${c.count === 1 ? 'tienda' : 'tiendas'}</span>
            </div>
          `;
        });
      }

      if (storeMatches.length > 0) {
        html += `<div class="distributor-locator__suggestions-group">Tiendas y Clínicas</div>`;
        storeMatches.forEach(s => {
          html += `
            <div class="distributor-locator__suggestion-item" data-type="store" data-value="${escAttr(s.nombre)}" data-lat="${s.lat ?? ''}" data-lng="${s.lng ?? ''}">
              <span class="distributor-locator__suggestion-icon">${ICON.pin}</span>
              <div class="distributor-locator__suggestion-text">
                <span class="distributor-locator__suggestion-title">${esc(s.nombre)}</span>
                <span class="distributor-locator__suggestion-sub">${esc([s.direccion, s.ciudad].filter(Boolean).join(', '))}</span>
              </div>
            </div>
          `;
        });
      }

      suggestionsEl.innerHTML = html;
      suggestionsEl.hidden = false;

      // Attach click listeners to suggestions
      suggestionsEl.querySelectorAll('.distributor-locator__suggestion-item').forEach(item => {
        item.addEventListener('click', () => {
          const val = item.dataset.value;
          const lat = parseFloat(item.dataset.lat);
          const lng = parseFloat(item.dataset.lng);

          if (searchInput) {
            searchInput.value = val;
            searchTerm = val;
          }
          if (searchClear) searchClear.hidden = false;
          suggestionsEl.hidden = true;

          applyFilters();

          // Smoothly fly map to city/store if coordinates are valid
          if (!isNaN(lat) && !isNaN(lng)) {
            if (activeMapType === 'google' && googleMap) {
              googleMap.panTo({ lat, lng });
              googleMap.setZoom(13);
            } else if (mapObj) {
              mapObj.flyTo([lat, lng], 13, { duration: 0.8 });
            }
          }
        });
      });
    }

    // --- Apply all active filters -----------------------------
    function applyFilters() {
      const term   = cleanStr(searchTerm);
      const region = activeRegion;
      const isSearchActive = Boolean(term || region);

      // If user typed a search term or selected a region, search globally across all Chilean stores
      let base = (!isSearchActive && isNearbyMode && userLocation)
        ? filterByRadius(allData, userLocation.lat, userLocation.lng, radiusKm)
        : allData;

      const filtered = base.filter(d => {
        const matchRegion = !region || d.region === region;
        const matchSearch = !term ||
          cleanStr(d.nombre).includes(term) ||
          cleanStr(d.ciudad).includes(term) ||
          cleanStr(d.direccion).includes(term) ||
          cleanStr(d.comuna).includes(term);
        return matchRegion && matchSearch;
      });

      // Update geoBanner visibility based on active search
      if (geoBanner) {
        if (isSearchActive) {
          geoBanner.hidden = true;
        } else if (isNearbyMode && userLocation) {
          geoBanner.hidden = false;
        }
      }

      renderCards(filtered, true);
    }

    function scheduleFilter() {
      clearTimeout(renderTimer);
      renderTimer = setTimeout(applyFilters, 160);
    }

    // --- Map Initializer Dispatcher & Fallback ----------------
    window.gm_authFailure = function () {
      console.warn('[DistributorLocator] Google Maps API error (gm_authFailure). Switching automatically to OpenStreetMap (Leaflet).');
      switchToLeafletFallback();
    };

    function switchToLeafletFallback() {
      if (activeMapType === 'leaflet' && mapObj) return;
      activeMapType = 'leaflet';

      // Remove Google error alert modal from DOM if rendered
      document.querySelectorAll('.gm-err-container').forEach(el => el.remove());

      if (mapEl) {
        mapEl.innerHTML = '';
        if (typeof window.L !== 'undefined') {
          initLeafletMap(mapEl);
          if (filteredData.length > 0) updateMapMarkers(filteredData);
          if (userLocation) updateUserLocationOnMap(userLocation, radiusKm);
        }
      }
    }

    function initMap(el) {
      if (mapProvider === 'google' && typeof window.google !== 'undefined' && typeof window.google.maps !== 'undefined') {
        initGoogleMap(el);
      } else if (typeof window.L !== 'undefined') {
        initLeafletMap(el);
      } else if (mapProvider === 'google' && googleApiKey) {
        // Retry when Google Maps script finishes loading
        let attempts = 0;
        const checkGoogle = setInterval(() => {
          attempts++;
          if (typeof window.google !== 'undefined' && typeof window.google.maps !== 'undefined') {
            clearInterval(checkGoogle);
            initGoogleMap(el);
            if (filteredData.length > 0) updateMapMarkers(filteredData);
          } else if (attempts >= 40) {
            clearInterval(checkGoogle);
            switchToLeafletFallback();
          }
        }, 150);
      }
    }

    // --- Google Maps Implementation --------------------------
    function initGoogleMap(el) {
      activeMapType = 'google';
      const zoom = parseInt(el.dataset.zoom, 10) || 6;
      const lat  = parseFloat(el.dataset.lat) || -35.6751;
      const lng  = parseFloat(el.dataset.lng) || -71.5430;

      googleMap = new google.maps.Map(el, {
        center: { lat, lng },
        zoom: zoom,
        mapTypeControl: false,
        streetViewControl: true,
        fullscreenControl: false,
        zoomControl: true,
        styles: [
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'simplified' }] }
        ]
      });

      googleInfoWindow = new google.maps.InfoWindow();

      googleMap.addListener('click', (e) => {
        if (!e.latLng) return;
        const clickedLat = e.latLng.lat();
        const clickedLng = e.latLng.lng();
        setUserSearchLocation({
          lat: clickedLat,
          lng: clickedLng,
          city: 'Punto seleccionado en el mapa',
          source: 'manual'
        });
      });

      const mapLoader = section.querySelector('.distributor-locator__map-loader');
      if (mapLoader) mapLoader.classList.add('is-loaded');
    }

    // --- OpenStreetMap (Leaflet) Implementation ---------------
    function initLeafletMap(el) {
      activeMapType = 'leaflet';
      const zoom = parseInt(el.dataset.zoom, 10) || 6;
      const lat  = parseFloat(el.dataset.lat) || -35.6751;
      const lng  = parseFloat(el.dataset.lng) || -71.5430;

      mapObj = L.map(el, {
        center: [lat, lng],
        zoom: zoom,
        scrollWheelZoom: false,
        zoomControl: true,
      });

      // OpenStreetMap tiles
      const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(mapObj);

      tileLayer.on('load', () => {
        const mapLoader = section.querySelector('.distributor-locator__map-loader');
        if (mapLoader) mapLoader.classList.add('is-loaded');
      });

      setTimeout(() => {
        const mapLoader = section.querySelector('.distributor-locator__map-loader');
        if (mapLoader) mapLoader.classList.add('is-loaded');
      }, 1000);

      mapObj.on('click', (e) => {
        if (!e.latlng) return;
        setUserSearchLocation({
          lat: e.latlng.lat,
          lng: e.latlng.lng,
          city: 'Punto seleccionado en el mapa',
          source: 'manual'
        });
      });

      markersLayer = L.featureGroup().addTo(mapObj);
    }

    function updateUserLocationOnMap(loc, km) {
      if (!loc) return;

      // Google Maps User Marker & Radius
      if (activeMapType === 'google' && googleMap) {
        if (googleUserMarker) googleUserMarker.setMap(null);
        if (googleRadiusCircle) googleRadiusCircle.setMap(null);

        googleUserMarker = new google.maps.Marker({
          position: { lat: loc.lat, lng: loc.lng },
          map: googleMap,
          title: 'Tu ubicación (arrástrame para mover)',
          draggable: true,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#033097',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2.5
          },
          zIndex: 1000
        });

        googleUserMarker.addListener('dragend', (e) => {
          if (!e.latLng) return;
          setUserSearchLocation({
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
            city: 'Punto seleccionado en el mapa',
            source: 'manual'
          });
        });

        if (isNearbyMode) {
          googleRadiusCircle = new google.maps.Circle({
            center: { lat: loc.lat, lng: loc.lng },
            radius: km * 1000,
            fillColor: '#033097',
            fillOpacity: 0.07,
            strokeColor: '#033097',
            strokeWeight: 1.5,
            map: googleMap
          });
        }
        return;
      }

      // Leaflet User Marker & Radius
      if (mapObj) {
        if (userMarker) mapObj.removeLayer(userMarker);
        if (radiusCircle) mapObj.removeLayer(radiusCircle);

        const userIcon = L.divIcon({
          className: 'dloc-user-marker-wrap',
          html: `<div class="dloc-user-marker-dot" title="Tu ubicación"><div class="dloc-user-marker-pulse"></div></div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11]
        });

        userMarker = L.marker([loc.lat, loc.lng], { icon: userIcon, zIndexOffset: 1000, draggable: true })
          .addTo(mapObj)
          .bindPopup(`<strong>Tu ubicación</strong>${loc.city ? '<br>' + esc(loc.city) : ''}<br><small>Arrástrame para mover</small>`);

        userMarker.on('dragend', (e) => {
          const pos = e.target.getLatLng();
          setUserSearchLocation({
            lat: pos.lat,
            lng: pos.lng,
            city: 'Punto seleccionado en el mapa',
            source: 'manual'
          });
        });

        if (isNearbyMode) {
          radiusCircle = L.circle([loc.lat, loc.lng], {
            radius: km * 1000,
            color: '#033097',
            fillColor: '#033097',
            fillOpacity: 0.07,
            weight: 1.5,
            dashArray: '5, 5'
          }).addTo(mapObj);
        }
      }
    }

    function updateMapMarkers(data) {
      if (activeMapType === 'none') return;

      markersMap.clear();

      // Group identical/near-identical coordinates to disperse markers nicely
      const coordGroups = new Map();
      data.forEach(d => {
        if (d.lat == null || d.lng == null || isNaN(d.lat) || isNaN(d.lng)) return;
        const key = `${Number(d.lat).toFixed(4)}_${Number(d.lng).toFixed(4)}`;
        if (!coordGroups.has(key)) coordGroups.set(key, []);
        coordGroups.get(key).push(d);
      });

      // ── Google Maps Render ──
      if (activeMapType === 'google' && googleMap) {
        googleMarkers.forEach(m => m.setMap(null));
        googleMarkers = [];

        const bounds = new google.maps.LatLngBounds();
        let validCount = 0;

        const pinSvgUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 42" width="30" height="42">
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M15 1C7.27 1 1 7.27 1 15c0 9.5 14 26 14 26S29 24.5 29 15C29 7.27 22.73 1 15 1z M15 9.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11z"
              fill="#d42b2b"
              stroke="#8b0000"
              stroke-width="1.2"
            />
          </svg>
        `);

        data.forEach((d, globalIdx) => {
          if (d.lat == null || d.lng == null || isNaN(d.lat) || isNaN(d.lng)) return;

          let markerLat = d.lat;
          let markerLng = d.lng;

          const key = `${Number(d.lat).toFixed(4)}_${Number(d.lng).toFixed(4)}`;
          const group = coordGroups.get(key);
          if (group && group.length > 1) {
            const indexInGroup = group.indexOf(d);
            const totalInGroup = group.length;
            const angle = (indexInGroup / totalInGroup) * 2 * Math.PI;
            const radiusOffset = 0.0022;
            markerLat = d.lat + Math.sin(angle) * radiusOffset;
            markerLng = d.lng + Math.cos(angle) * (radiusOffset * 1.25);
          }

          const marker = new google.maps.Marker({
            position: { lat: markerLat, lng: markerLng },
            map: googleMap,
            title: d.nombre,
            icon: {
              url: pinSvgUrl,
              scaledSize: new google.maps.Size(30, 42),
              anchor: new google.maps.Point(15, 42)
            }
          });

          marker.addListener('click', () => {
            googleInfoWindow.setContent(buildPopupContent(d));
            googleInfoWindow.open(googleMap, marker);
            handleMarkerClick(globalIdx);
          });

          googleMarkers.push(marker);
          markersMap.set(globalIdx, marker);
          bounds.extend({ lat: markerLat, lng: markerLng });
          validCount++;
        });

        const isSearchActive = Boolean(searchTerm || activeRegion);
        if (validCount > 0) {
          if (!isSearchActive && userLocation && isNearbyMode) {
            bounds.extend({ lat: userLocation.lat, lng: userLocation.lng });
            googleMap.fitBounds(bounds);
          } else if (activeRegion || searchTerm) {
            googleMap.fitBounds(bounds);
          } else {
            googleMap.setCenter({ lat: -35.6751, lng: -71.5430 });
            googleMap.setZoom(6);
          }
        }
        return;
      }

      // ── Leaflet Render ──
      if (activeMapType === 'leaflet' && mapObj && markersLayer) {
        markersLayer.clearLayers();
        const validCoords = [];

        data.forEach((d, globalIdx) => {
          if (d.lat == null || d.lng == null || isNaN(d.lat) || isNaN(d.lng)) return;

          let markerLat = d.lat;
          let markerLng = d.lng;

          const key = `${Number(d.lat).toFixed(4)}_${Number(d.lng).toFixed(4)}`;
          const group = coordGroups.get(key);
          if (group && group.length > 1) {
            const indexInGroup = group.indexOf(d);
            const totalInGroup = group.length;
            const angle = (indexInGroup / totalInGroup) * 2 * Math.PI;
            const radiusOffset = 0.0022;
            markerLat = d.lat + Math.sin(angle) * radiusOffset;
            markerLng = d.lng + Math.cos(angle) * (radiusOffset * 1.25);
          }

          const pinHtml = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 42" width="30" height="42" style="overflow:visible;display:block;">
              <defs>
                <filter id="pin-shadow-${globalIdx}" x="-30%" y="-10%" width="160%" height="140%">
                  <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="rgba(0,0,0,0.4)"/>
                </filter>
              </defs>
              <path fill-rule="evenodd" clip-rule="evenodd"
                d="M15 1C7.27 1 1 7.27 1 15c0 9.5 14 26 14 26S29 24.5 29 15C29 7.27 22.73 1 15 1z M15 9.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11z"
                fill="#d42b2b"
                stroke="#8b0000"
                stroke-width="1"
                stroke-linejoin="round"
                filter="url(#pin-shadow-${globalIdx})"
              />
            </svg>`;

          const markerIcon = L.divIcon({
            className: 'dloc-leaflet-pin-icon',
            html: pinHtml,
            iconSize: [30, 42],
            iconAnchor: [15, 42],
            popupAnchor: [0, -44]
          });

          const marker = L.marker([markerLat, markerLng], { icon: markerIcon });
          marker.bindPopup(buildPopupContent(d), { maxWidth: 300, className: 'dloc-custom-leaflet-popup' });

          marker.on('click', () => {
            handleMarkerClick(globalIdx);
          });

          markersLayer.addLayer(marker);
          markersMap.set(globalIdx, marker);
          validCoords.push([markerLat, markerLng]);
        });

        if (validCoords.length > 0) {
          try {
            const isSearchActive = Boolean(searchTerm || activeRegion);
            if (!isSearchActive && userLocation && isNearbyMode) {
              const bounds = L.latLngBounds(validCoords);
              bounds.extend([userLocation.lat, userLocation.lng]);
              mapObj.fitBounds(bounds, { padding: [40, 40], maxZoom: 13, animate: true });
            } else if (activeRegion || searchTerm) {
              mapObj.fitBounds(markersLayer.getBounds(), { padding: [40, 40], maxZoom: 14, animate: true });
            } else {
              // All regions view: center comfortably on Chile
              mapObj.setView([-35.6751, -71.5430], 6);
            }
          } catch (e) {
            // ignore fitBounds error
          }
        }
      }
    }

    function highlightMarker(globalIdx, openPopup) {
      const distData = filteredData[globalIdx] || allData[globalIdx];
      if (!distData) return;

      const marker = markersMap.get(globalIdx);

      if (activeMapType === 'google' && googleMap) {
        if (distData.lat != null && distData.lng != null && !isNaN(distData.lat) && !isNaN(distData.lng)) {
          const pos = marker ? marker.getPosition() : { lat: distData.lat, lng: distData.lng };
          if (openPopup) {
            googleMap.setZoom(15);
            googleMap.panTo(pos);
            if (marker) {
              googleInfoWindow.setContent(buildPopupContent(distData));
              googleInfoWindow.open(googleMap, marker);
            }
          } else {
            googleMap.panTo(pos);
          }
        }
      } else if (activeMapType === 'leaflet' && mapObj) {
        if (distData.lat != null && distData.lng != null && !isNaN(distData.lat) && !isNaN(distData.lng)) {
          const pos = marker ? marker.getLatLng() : [distData.lat, distData.lng];
          if (openPopup) {
            mapObj.flyTo(pos, 15, { animate: true, duration: 0.8 });
            if (marker) {
              setTimeout(() => {
                marker.openPopup();
              }, 350);
            }
          } else {
            mapObj.panTo(pos, { animate: true, duration: 0.4 });
          }
        }
      }
    }

    function handleMarkerClick(globalIdx) {
      const targetPage = Math.floor(globalIdx / itemsPerPage) + 1;
      if (targetPage !== currentPage) {
        currentPage = targetPage;
        renderCards(filteredData, false);
      }

      highlightCard(globalIdx);
    }

    function highlightCard(globalIdx) {
      const card = grid.querySelector(`#dloc-card-${globalIdx}`);
      if (!card) return;

      grid.querySelectorAll('.distributor-card').forEach(c => c.classList.remove('is-active-card'));
      card.classList.add('is-active-card');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // --- Tab Switching on Mobile ------------------------------
    function activateTab(tab) {
      tabButtons.forEach(btn => {
        const isActive = btn.dataset.tab === tab;
        btn.classList.toggle('is-active', isActive);
      });

      if (cardsPane && mapPane) {
        if (tab === 'map') {
          cardsPane.style.display = 'none';
          mapPane.style.display = 'block';
          if (activeMapType === 'google' && googleMap) {
            setTimeout(() => { google.maps.event.trigger(googleMap, 'resize'); }, 50);
          } else if (mapObj) {
            setTimeout(() => { mapObj.invalidateSize(); }, 50);
          }
        } else {
          cardsPane.style.display = 'block';
          mapPane.style.display = 'none';
        }
      }
    }

    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        activateTab(btn.dataset.tab);
      });
    });

    // --- Event listeners --------------------------------------
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        searchTerm = searchInput.value.trim();
        if (searchClear) searchClear.hidden = !searchTerm;
        renderSuggestions(searchTerm);
        scheduleFilter();
      });

      // Close suggestions on blur or click outside
      document.addEventListener('click', (e) => {
        if (!section.querySelector('.distributor-locator__search-wrap')?.contains(e.target)) {
          if (suggestionsEl) suggestionsEl.hidden = true;
        }
      });

      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (suggestionsEl) suggestionsEl.hidden = true;
        } else if (e.key === 'Enter') {
          const val = searchInput.value.trim();
          if (val) {
            geocodeAddress(val);
          }
        }
      });
    }

    if (searchClear) {
      searchClear.addEventListener('click', () => {
        if (searchInput) searchInput.value = '';
        searchTerm = '';
        searchClear.hidden = true;
        if (suggestionsEl) suggestionsEl.hidden = true;
        scheduleFilter();
      });
    }

    if (regionSelect) {
      regionSelect.addEventListener('change', () => {
        activeRegion = regionSelect.value;
        scheduleFilter();
      });
    }

    // "Ver todos" - exit nearby mode
    if (geoShowAll) {
      geoShowAll.addEventListener('click', () => {
        isNearbyMode = false;
        geoShowAll.hidden = true;
        geoBanner.hidden = true;
        renderCards(allData, true);
        if (radiusCircle && mapObj) {
          mapObj.removeLayer(radiusCircle);
        }
        if (googleRadiusCircle) {
          googleRadiusCircle.setMap(null);
          googleRadiusCircle = null;
        }
      });
    }

    // "Detectar ubicación" - re-trigger geolocation
    if (geoLocate) {
      geoLocate.addEventListener('click', async () => {
        showGeoStatus('loading');
        userLocation = await detectLocation();
        if (userLocation) {
          const nearby = filterByRadius(allData, userLocation.lat, userLocation.lng, radiusKm);
          if (nearby.length > 0) {
            isNearbyMode = true;
            showGeoStatus('found', userLocation, nearby.length, radiusKm);
            renderCards(nearby, true);
          } else {
            showGeoStatus('noneNearby', userLocation, 0, radiusKm);
            renderCards(allData, true);
          }
          updateUserLocationOnMap(userLocation, radiusKm);
        } else {
          showGeoStatus('denied');
        }
      });
    }

    // Close banner
    if (geoBannerClose) {
      geoBannerClose.addEventListener('click', () => {
        if (geoBanner) geoBanner.hidden = true;
      });
    }

    // Empty state reset
    if (emptyState) {
      const resetBtn = emptyState.querySelector('.distributor-locator__empty-reset');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          if (searchInput) searchInput.value = '';
          if (regionSelect) regionSelect.value = '';
          if (searchClear) searchClear.hidden = true;
          if (suggestionsEl) suggestionsEl.hidden = true;
          searchTerm   = '';
          activeRegion = '';
          isNearbyMode = false;
          renderCards(allData, true);
        });
      }
    }
  }

  // --- Bootstrap on DOMReady & Shopify Section Events -------
  function bootstrap() {
    document.querySelectorAll('.distributor-locator').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', e => {
    const s = e.target.querySelector('.distributor-locator');
    if (s) init(s);
  });
})();
